import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Chip,
  Rating,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Skeleton,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Tooltip,
  CircularProgress,
  Fade,
  ButtonGroup,
} from '@mui/material';
import {
  LocationOn,
  Schedule,
  CalendarMonth,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ViewList as ViewListIcon,
  Share,
  Message,
  Work,
  VerifiedUser,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import ReportDialog from '../../components/ReportDialog';
import PageBackButton from '../../components/PageBackButton';
import { agendaService } from '../../services/agendaService';

// Интерфейс за потребителя
interface User {
  id: string;
  email?: string;
  username?: string;
  full_name?: string;
  user_type?: string;
  // ... other user properties
}

// Интерфейс за данните на специалиста
interface Professional {
  id: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  avatar_url: string;
  description: string;
  services: string[];
  pricing: Array<{
    service: string;
    price: string;
  }>;
  availability: {
    days: string;
    hours: string;
  };
  qualifications: string[];
  experience: any[];
  portfolio: any[];
  reviews: any[];
  rating: number;
  total_reviews: number;
  completed_jobs: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // ... other auth context properties
}

// Добавяме нов тип за снимка в портфолиото
interface PortfolioItem {
  img: string;
  description: string;
  title?: string;
}

interface AgendaEntry {
  starts_at: string;
  ends_at?: string | null;
  status: 'planned' | 'busy' | 'completed' | 'cancelled';
}

interface PublicAvailability {
  works_monday: boolean;
  works_tuesday: boolean;
  works_wednesday: boolean;
  works_thursday: boolean;
  works_friday: boolean;
  works_saturday: boolean;
  works_sunday: boolean;
  holiday_country: string;
  non_working_dates: string[];
  working_override_dates: string[];
  non_working_exceptions: Array<{
    date: string;
    label?: string | null;
  }>;
}

type PublicAvailabilityWeekdayField =
  | 'works_monday'
  | 'works_tuesday'
  | 'works_wednesday'
  | 'works_thursday'
  | 'works_friday'
  | 'works_saturday'
  | 'works_sunday';

const toDateOnlyKey = (value: Date): string => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isBusyStatus = (status: AgendaEntry['status']): boolean => status === 'planned' || status === 'busy';
const weekdayFieldByIndex: PublicAvailabilityWeekdayField[] = [
  'works_monday',
  'works_tuesday',
  'works_wednesday',
  'works_thursday',
  'works_friday',
  'works_saturday',
  'works_sunday',
];

const ProfessionalProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useAuth() as AuthContextType;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Partial<Professional>>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'service' | 'qualification' | 'portfolio' | 'avatar'>('service');
  const [newItem, setNewItem] = useState('');
  const [selectedImage, setSelectedImage] = useState<PortfolioItem | null>(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [publicAgendaView, setPublicAgendaView] = useState<'month' | 'week'>('week');
  const [publicAgendaCursor, setPublicAgendaCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Използваме ID от URL параметъра
  const professionalId = id || '';

  // Проверка при първоначално зареждане дали потребителят е клиент
  useEffect(() => {
    // Проверяваме дали потребителят гледа собствения си профил
    if (user && user.id.toString() === professionalId) {
      // Ако имейлът не е на професионалист и user_type не е сменен на professional, пренасочваме
      const currentUserType = user.user_type || localStorage.getItem('user_type') || 
                            (user.email === 'mitat1976@abv.bg' ? 'professional' : 'client');
      
      if (currentUserType !== 'professional') {
        navigate(`/profile/client/${professionalId}/dashboard`);
      }
    }
  }, [user, professionalId, navigate]);

  // Зареждане на данните на специалиста
  const { data: professional, isLoading, error } = useQuery<Professional | null>({
    queryKey: ['professional', professionalId],
    queryFn: async () => {
      if (!professionalId) {
        throw new Error('No professional ID provided');
      }
      
      try {
        const response = await api.get(`/api/v1/professionals/${professionalId}`);
        return response.data;
      } catch (error: any) {
        const statusCode = error?.response?.status;
        if (statusCode === 404) {
          return null;
        }
        console.error('Error fetching professional data:', error);
        throw error;
      }
    },
    enabled: !!professionalId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: agendaEntries = [], isLoading: agendaLoading } = useQuery<AgendaEntry[]>({
    queryKey: ['publicAgenda', professionalId],
    queryFn: async () => {
      if (!professionalId) return [];
      const parsedId = Number(professionalId);
      if (Number.isNaN(parsedId)) return [];
      return agendaService.getPublicEntries(parsedId);
    },
    enabled: !!professionalId,
  });

  const { data: publicAvailability } = useQuery<PublicAvailability>({
    queryKey: ['publicAgendaAvailability', professionalId],
    queryFn: async () => {
      if (!professionalId) {
        return {
          works_monday: true,
          works_tuesday: true,
          works_wednesday: true,
          works_thursday: true,
          works_friday: true,
          works_saturday: false,
          works_sunday: false,
          holiday_country: 'BG',
          non_working_dates: [],
          working_override_dates: [],
          non_working_exceptions: [],
        };
      }
      const parsedId = Number(professionalId);
      if (Number.isNaN(parsedId)) {
        return {
          works_monday: true,
          works_tuesday: true,
          works_wednesday: true,
          works_thursday: true,
          works_friday: true,
          works_saturday: false,
          works_sunday: false,
          holiday_country: 'BG',
          non_working_dates: [],
          working_override_dates: [],
          non_working_exceptions: [],
        };
      }
      return agendaService.getPublicAvailability(parsedId);
    },
    enabled: !!professionalId,
    retry: false,
  });

  const publicBusyDays = React.useMemo(() => {
    const busyMap = new Map<string, boolean>();
    agendaEntries.forEach((slot) => {
      if (!isBusyStatus(slot.status)) return;
      const start = new Date(slot.starts_at);
      const end = slot.ends_at ? new Date(slot.ends_at) : start;
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

      const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      while (current.getTime() <= endDay.getTime()) {
        busyMap.set(toDateOnlyKey(current), true);
        current.setDate(current.getDate() + 1);
      }
    });
    return busyMap;
  }, [agendaEntries]);

  const publicNonWorkingDates = React.useMemo(() => new Set(publicAvailability?.non_working_dates || []), [publicAvailability]);
  const publicWorkingOverrides = React.useMemo(
    () => new Set(publicAvailability?.working_override_dates || []),
    [publicAvailability]
  );
  const publicHolidayLabelByDate = React.useMemo(() => {
    const map = new Map<string, string>();
    (publicAvailability?.non_working_exceptions || []).forEach((item) => {
      if (item.label && item.label.trim()) {
        map.set(item.date, item.label.trim());
      }
    });
    return map;
  }, [publicAvailability]);

  const isPublicNonWorkingDay = (day: Date): boolean => {
    const key = toDateOnlyKey(day);
    if (publicWorkingOverrides.has(key)) return false;
    if (publicNonWorkingDates.has(key)) return true;
    if (!publicAvailability) return day.getDay() === 0 || day.getDay() === 6;
    const weekdayIndex = (day.getDay() + 6) % 7;
    const field = weekdayFieldByIndex[weekdayIndex];
    return !publicAvailability[field];
  };

  const publicMonthDays = React.useMemo(() => {
    const year = publicAgendaCursor.getFullYear();
    const month = publicAgendaCursor.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const firstWeekday = (first.getDay() + 6) % 7;
    const days: Date[] = [];

    for (let i = 0; i < firstWeekday; i += 1) {
      days.push(new Date(year, month, i - firstWeekday + 1));
    }
    for (let day = 1; day <= last.getDate(); day += 1) {
      days.push(new Date(year, month, day));
    }
    while (days.length % 7 !== 0) {
      days.push(new Date(year, month + 1, days.length - (firstWeekday + last.getDate()) + 1));
    }
    return days;
  }, [publicAgendaCursor]);

  const publicWeekDays = React.useMemo(() => {
    const cursor = new Date(publicAgendaCursor);
    const weekday = (cursor.getDay() + 6) % 7;
    const weekStart = new Date(cursor);
    weekStart.setDate(cursor.getDate() - weekday);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + index);
      return date;
    });
  }, [publicAgendaCursor]);

  const publicWeekdayHeaders = React.useMemo(() => {
    const baseDate = new Date(2026, 0, 5);
    return Array.from({ length: 7 }, (_, index) =>
      new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + index).toLocaleDateString(undefined, {
        weekday: 'short',
      })
    );
  }, []);

  const isViewingOwnProfile = !!(user?.id && professionalId && user.id.toString() === professionalId);

  // Мутация за обновяване на профила
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Professional>) => {
      if (!professionalId) {
        throw new Error('No professional ID provided');
      }
      try {
        const response = await api.patch(`/api/v1/professionals/${professionalId}`, data);
        return response.data;
      } catch (error: any) {
        console.error('Грешка от API при обновяване на профил:', error?.response?.data || error);
        // Подобряваме съобщението за грешка
        if (error?.response?.data?.detail) {
          throw new Error(`API error: ${error.response.data.detail}`);
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional', professionalId] });
      setIsEditing(false);
    },
    onError: (error: any) => {
      console.error('Грешка при обновяване на профил:', error);
    }
  });

  const handleEdit = () => {
    setEditedData(professional || {});
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (editedData) {
      try {
        // Подготвяме данните за изпращане
        const dataToSend = {
          name: editedData.name,
          email: editedData.email,
          phone: editedData.phone,
          location: editedData.location,
          avatar_url: editedData.avatar_url,
          description: editedData.description,
          services: editedData.services,
          pricing: editedData.pricing,
          availability: editedData.availability,
          qualifications: editedData.qualifications,
          experience: editedData.experience,
          portfolio: editedData.portfolio,
          user_type: "professional" // Добавяме тип потребител
        };
        
        // Първо обновяваме потребителския профил чрез /api/v1/me
        try {
          await api.patch('/api/v1/me', {
            name: dataToSend.name,
            user_type: "professional",
            phone: dataToSend.phone,
            location: dataToSend.location,
            avatar_url: dataToSend.avatar_url
          });
          // Запазваме аватара в localStorage
          if (dataToSend.avatar_url) {
            localStorage.setItem('professional_avatar_url', dataToSend.avatar_url);
          }
          
          // Обновяваме кеша с новите данни от потребителския профил
          queryClient.setQueryData(['professional', professionalId], (oldData: any) => {
            if (oldData) {
              return { 
                ...oldData,
                name: dataToSend.name,
                email: dataToSend.email,
                phone: dataToSend.phone,
                location: dataToSend.location,
                avatar_url: dataToSend.avatar_url
              };
            }
            return oldData;
          });
          
          // Опитваме да обновим и професионалиста
          try {
            await updateMutation.mutateAsync(dataToSend);
            alert(t('profile.save_success'));
            
            // Обновяваме целия кеш след успешно запазване
            queryClient.invalidateQueries({ queryKey: ['professional', professionalId] });
            
            // Излизаме от режим на редактиране
            setIsEditing(false);
          } catch (updateError) {
            console.error('Грешка при обновяване на професионалист:', updateError);
            
            // Основните данни са записани успешно
            alert(t('profile.save_partial_success'));
            setIsEditing(false);
          }
        } catch (meError) {
          console.error('Грешка при обновяване на потребителския профил:', meError);
          
          // Опитваме само обновяване на професионалиста
          try {
            await updateMutation.mutateAsync(dataToSend);
            alert(t('profile.save_success'));
            setIsEditing(false);
          } catch (profError) {
            console.error('Грешка при обновяване на професионалист след неуспешно обновяване на потребителски профил:', profError);
            alert(t('profile.save_error'));
          }
        }
      } catch (error) {
        console.error('Обща грешка при опит за записване:', error);
        alert(t('profile.unexpected_save_error'));
      }
    }
  };

  const handleCancel = () => {
    setEditedData({});
    setIsEditing(false);
  };

  const handleChange = (field: keyof Professional, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handlePricingChange = (index: number, field: 'service' | 'price', value: string) => {
    const currentPricing = [...(editedData.pricing || [])];
    const currentItem = currentPricing[index] || { service: '', price: '' };
    currentPricing[index] = { ...currentItem, [field]: value };
    handleChange('pricing', currentPricing);
  };

  const handleAddPricingItem = () => {
    handleChange('pricing', [...(editedData.pricing || []), { service: '', price: '' }]);
  };

  const handleRemovePricingItem = (index: number) => {
    handleChange('pricing', (editedData.pricing || []).filter((_, i) => i !== index));
  };

  const handleAvailabilityChange = (field: 'days' | 'hours', value: string) => {
    const currentAvailability = editedData.availability || { days: '', hours: '' };
    handleChange('availability', { ...currentAvailability, [field]: value });
  };

  const handleExperienceChange = (index: number, field: 'title' | 'company' | 'period', value: string) => {
    const currentExperience = [...(editedData.experience || [])];
    const currentItem = currentExperience[index] || { title: '', company: '', period: '' };
    currentExperience[index] = { ...currentItem, [field]: value };
    handleChange('experience', currentExperience);
  };

  const handleAddExperienceItem = () => {
    handleChange('experience', [...(editedData.experience || []), { title: '', company: '', period: '' }]);
  };

  const handleRemoveExperienceItem = (index: number) => {
    handleChange('experience', (editedData.experience || []).filter((_, i) => i !== index));
  };

  const handleAddItem = () => {
    if (dialogType === 'service' && newItem) {
      handleChange('services', [...(editedData.services || []), newItem]);
    } else if (dialogType === 'qualification' && newItem) {
      handleChange('qualifications', [...(editedData.qualifications || []), newItem]);
    } else if (dialogType === 'avatar') {
      // За аватар, вече е приложено чрез текстовото поле
    }
    
    setNewItem('');
    setOpenDialog(false);
  };

  const handleRemoveItem = (type: string, index: number) => {
    switch (type) {
      case 'service':
        handleChange('services', (editedData.services || []).filter((_, i) => i !== index));
        break;
      case 'qualification':
        handleChange('qualifications', (editedData.qualifications || []).filter((_, i) => i !== index));
        break;
      case 'portfolio':
        handleChange('portfolio', (editedData.portfolio || []).filter((_, i) => i !== index));
        break;
    }
  };

  // Функция за качване на снимка
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadProgress(0);
      
      // Променяме API пътя от '/api/v1/upload' на '/api/v1/me/avatar'
      const response = await api.post('/api/v1/me/avatar', formData, {
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      });

      // Добавяме новата снимка към портфолиото
      const newImage = {
        img: response.data.url, // URL на качената снимка
        description: file.name, // Използваме името на файла като описание
        title: file.name.split('.')[0] // Използваме името на файла без разширението като заглавие
      };

      handleChange('portfolio', [...(editedData.portfolio || []), newImage]);
      setUploadProgress(null);
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadProgress(null);
    }
  };

  // Функция за отваряне на файлов диалог
  const handleOpenFileDialog = () => {
    if (fileInputRef.current) {
      // Затваряме диалоговия прозорец преди да отворим файловия диалог
      setOpenDialog(false);
      // Малко закъснение преди да отворим файловия диалог, за да може диалогът да се затвори правилно
      setTimeout(() => {
        // Проверяваме дали трябва да качим аватар или снимка за портфолиото
        if (fileInputRef.current) {
          if (dialogType === 'avatar') {
            // За аватар използваме специалния хендлър
            fileInputRef.current.setAttribute('data-upload-type', 'avatar');
          } else {
            // За портфолио снимки използваме общия хендлър
            fileInputRef.current.setAttribute('data-upload-type', 'portfolio');
          }
          fileInputRef.current.click();
        }
      }, 100);
    }
  };

  // Функция за отваряне на модалния прозорец със снимка
  const handleOpenImageModal = (item: PortfolioItem) => {
    setSelectedImage(item);
    setOpenImageModal(true);
  };

  // Функция за затваряне на модалния прозорец със снимка
  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setSelectedImage(null);
  };

  // Функция за смяна на аватар
  const handleAvatarClick = () => {
    // Позволява смяна на аватар само, ако потребителят е в режим на редактиране
    if (isEditing) {
      // Тук показваме диалог за въвеждане на URL или качване на файл
      setDialogType('avatar');
      setOpenDialog(true);
    }
  };

  // Функция за смяна на аватар с качена снимка
  const handleAvatarFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadProgress(0);
      
      // Използваме общия endpoint за потребителски аватар
      const response = await api.post(`/api/v1/me/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      });
      
      // Проверяваме дали отговорът съдържа avatar_url поле
      if (response && response.data && response.data.avatar_url) {
        // Актуализираме аватара в редактираните данни
        const avatarUrl = response.data.avatar_url;
        
        // Добавяме timestamp към URL за да избегнем кеширане
        const timestampedUrl = `${avatarUrl}?t=${new Date().getTime()}`;
        
        // Запазваме URL-то на аватара в localStorage
        localStorage.setItem('professional_avatar_url', timestampedUrl);
        
        handleChange('avatar_url', timestampedUrl);
        
        // Обновяваме аватара и в локалния state, за да се покаже веднага
        setEditedData(prev => ({ ...prev, avatar_url: timestampedUrl }));
        
        // Обновяваме и в queryClient кеша, за да се отрази при следващо зареждане
        queryClient.setQueryData(['professional', professionalId], (oldData: any) => {
          if (oldData) {
            return { ...oldData, avatar_url: timestampedUrl };
          }
          return oldData;
        });
        
        // Затваряме диалога и показваме съобщение за успех
        setOpenDialog(false);
        alert(t('profile.avatar_upload_success'));
        return;
      } else {
        console.error('Неочакван формат на отговор от сървъра:', response.data);
        alert(t('profile.avatar_upload_invalid_response'));
      }
      
      setUploadProgress(null);
    } catch (error) {
      console.error('Грешка при качване на аватар:', error);
      alert(t('profile.avatar_upload_error'));
      setUploadProgress(null);
      setOpenDialog(false);
    }
  };

  // Показване на зареждащ индикатор
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageBackButton />
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Skeleton variant="circular" width={200} height={200} />
              <Skeleton variant="rectangular" height={40} sx={{ mt: 2, mb: 1 }} />
              <Skeleton variant="rectangular" height={40} />
            </Grid>
            <Grid item xs={12} md={9}>
              <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={30} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={100} />
            </Grid>
          </Grid>
        </Paper>
      </Container>
    );
  }

  // Показване на грешка
  if (error) {
    const statusCode = (error as any)?.response?.status;
    if (statusCode === 404) {
      return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <PageBackButton />
          <Alert severity="info">
            {t('common.no_results')}
          </Alert>
          <Typography>{t('profile.not_found')}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            {t('common.go_to_home')}
          </Button>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageBackButton />
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common.error')}
        </Alert>
        <Typography>{t('profile.load_error_details')}</Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
          {t('common.error')}: {(error as Error).message}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          {t('common.go_to_home')}
        </Button>
      </Container>
    );
  }

  if (!professional) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageBackButton />
        <Alert severity="info">
          {t('common.no_results')}
        </Alert>
        <Typography>{t('profile.not_found')}</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          {t('common.go_to_home')}
        </Button>
      </Container>
    );
  }

  const isOwner = isViewingOwnProfile || !!(user?.id && professional?.id && user.id.toString() === professional.id.toString());

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageBackButton />
      {/* Основна информация */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                src={isEditing ? editedData.avatar_url : professional?.avatar_url}
                sx={{
                  width: 200,
                  height: 200,
                  mb: 2,
                  border: '3px solid',
                  borderColor: 'primary.main',
                  cursor: isEditing ? 'pointer' : 'default',
                  ...(isEditing && {
                    '&:hover': {
                      opacity: 0.8,
                      boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.3)',
                    }
                  })
                }}
                onClick={handleAvatarClick}
              />
              {isEditing && (
                <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('profile.avatar_url')}
                    value={editedData.avatar_url || ''}
                    onChange={(e) => handleChange('avatar_url', e.target.value)}
                  />
                  <IconButton 
                    color="primary" 
                    size="small" 
                    sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
                    onClick={handleAvatarClick}
                  >
                    <CloudUploadIcon />
                  </IconButton>
                </Box>
              )}
              {/* Input for file upload */}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={(e) => {
                  // Определяме типа на качването според data-атрибута
                  const uploadType = e.target.getAttribute('data-upload-type');
                  if (uploadType === 'avatar') {
                    handleAvatarFileUpload(e);
                  } else {
                    handleFileUpload(e);
                  }
                }}
              />
              {!isEditing && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Message />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {t('profile.contact')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Share />}
                    fullWidth
                  >
                    {t('profile.share')}
                  </Button>
                  {!isOwner && user && (
                    <Button
                      variant="outlined"
                      color="warning"
                      fullWidth
                      sx={{ mt: 1 }}
                      onClick={() => setIsReportDialogOpen(true)}
                    >
                      {t('admin.reports.report_profile_button')}
                    </Button>
                  )}
                </>
              )}
              {isOwner && !isEditing && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  fullWidth
                  onClick={handleEdit}
                  sx={{ mt: 1 }}
                  data-testid="profile-edit-button"
                >
                  {t('profile.edit')}
                </Button>
              )}
              {isEditing && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, width: '100%' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    fullWidth
                    data-testid="profile-save-button"
                  >
                    {t('common.save')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    fullWidth
                  >
                    {t('common.cancel')}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box>
                {isEditing ? (
                  <>
                    <TextField
                      fullWidth
                      label={t('common.full_name')}
                      value={editedData.name || ''}
                      onChange={(e) => handleChange('name', e.target.value)}
                      sx={{ mb: 2 }}
                    />
                  </>
                ) : (
                  <>
                    <Typography variant="h4" gutterBottom>
                      {professional?.name}
                    </Typography>
                  </>
                )}
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Rating value={professional?.rating} precision={0.1} readOnly />
                <Typography variant="body2" color="text.secondary">
                  {t('profile.reviews_count', { count: professional?.total_reviews || 0 })}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOn color="action" />
              {isEditing ? (
                <TextField
                  fullWidth
                  label={t('common.location')}
                  value={editedData.location || ''}
                  onChange={(e) => handleChange('location', e.target.value)}
                  sx={{ ml: 1 }}
                />
              ) : (
                <Typography variant="body1" component="div" sx={{ ml: 1 }}>
                  {professional?.location}
                </Typography>
              )}
            </Box>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('profile.description')}
                value={editedData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                sx={{ mb: 2 }}
              />
            ) : (
              <Typography variant="body1" component="div" paragraph>
                {professional?.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {(isEditing ? editedData.services : professional?.services)?.map((service, index) => (
                <Chip
                  key={index}
                  label={service}
                  color="primary"
                  variant="outlined"
                  onDelete={isEditing ? () => handleRemoveItem('service', index) : undefined}
                />
              ))}
              {isEditing && (
                <IconButton
                  color="primary"
                  onClick={() => {
                    setDialogType('service');
                    setOpenDialog(true);
                  }}
                >
                  <AddIcon />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Услуги и цени */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('profile.services_pricing')}
            </Typography>
            {isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(editedData.pricing || []).map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('common.service')}
                      value={item.service || ''}
                      onChange={(e) => handlePricingChange(index, 'service', e.target.value)}
                      inputProps={{ 'data-testid': `profile-pricing-service-input-${index}` }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label={t('common.price')}
                      value={item.price || ''}
                      onChange={(e) => handlePricingChange(index, 'price', e.target.value)}
                      inputProps={{ 'data-testid': `profile-pricing-price-input-${index}` }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleRemovePricingItem(index)}
                      aria-label={t('common.delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddPricingItem}
                  sx={{ alignSelf: 'flex-start' }}
                  data-testid="profile-add-pricing-button"
                >
                  {t('common.add')}
                </Button>
              </Box>
            ) : (
              <List>
                {professional.pricing.map((item, index) => (
                  <ListItem key={index} divider={index !== professional.pricing.length - 1}>
                    <ListItemText
                      primary={item.service}
                      secondary={item.price}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('profile.availability')}
            </Typography>
            {isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('profile.availability_days')}
                  value={editedData.availability?.days || ''}
                  onChange={(e) => handleAvailabilityChange('days', e.target.value)}
                  inputProps={{ 'data-testid': 'profile-availability-days-input' }}
                />
                <TextField
                  fullWidth
                  label={t('profile.working_hours')}
                  value={editedData.availability?.hours || ''}
                  onChange={(e) => handleAvailabilityChange('hours', e.target.value)}
                  inputProps={{ 'data-testid': 'profile-availability-hours-input' }}
                />
              </Box>
            ) : (
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('profile.working_hours')}
                    secondary={`${professional?.availability?.days}, ${professional?.availability?.hours}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('profile.emergency_availability')}
                    secondary={t('profile.contact_for_emergency')}
                  />
                </ListItem>
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 3, my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">{t('agenda.profile_section_title')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ButtonGroup variant="outlined" size="small">
              <Button
                onClick={() => setPublicAgendaView('month')}
                startIcon={<CalendarMonth />}
                variant={publicAgendaView === 'month' ? 'contained' : 'outlined'}
              >
                {t('agenda.view.month')}
              </Button>
              <Button
                onClick={() => setPublicAgendaView('week')}
                startIcon={<ViewListIcon />}
                variant={publicAgendaView === 'week' ? 'contained' : 'outlined'}
              >
                {t('agenda.view.week')}
              </Button>
            </ButtonGroup>
            {isOwner && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalendarMonth />}
                onClick={() => navigate(`/profile/professional/${professionalId}/agenda`)}
              >
                {t('agenda.manage')}
              </Button>
            )}
          </Box>
        </Box>
        {agendaLoading ? (
          <Skeleton variant="rectangular" height={80} />
        ) : (
          <>
            {agendaEntries.length === 0 && (
              <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                {t('agenda.public_empty')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Button
                size="small"
                startIcon={<ChevronLeftIcon />}
                onClick={() =>
                  setPublicAgendaCursor((prev) =>
                    publicAgendaView === 'month'
                      ? new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                      : new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 7)
                  )
                }
              >
                {t('common.back')}
              </Button>
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                {publicAgendaView === 'month'
                  ? publicAgendaCursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
                  : t('agenda.week_of', {
                      date: publicWeekDays[0]?.toLocaleDateString(),
                    })}
              </Typography>
              <Button
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() =>
                  setPublicAgendaCursor((prev) =>
                    publicAgendaView === 'month'
                      ? new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                      : new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 7)
                  )
                }
              >
                {t('common.next')}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('agenda.public_privacy_hint')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 0.75,
              }}
            >
              {publicWeekdayHeaders.map((header) => (
                <Typography key={header} variant="caption" sx={{ fontWeight: 600 }}>
                  {header}
                </Typography>
              ))}
              {(publicAgendaView === 'month' ? publicMonthDays : publicWeekDays).map((day) => {
                const dayKey = toDateOnlyKey(day);
                const isBusy = Boolean(publicBusyDays.get(dayKey));
                const isNonWorking = isPublicNonWorkingDay(day);
                const holidayLabel = publicHolidayLabelByDate.get(dayKey);
                const isCurrentMonth = day.getMonth() === publicAgendaCursor.getMonth();
                const chipColor = isBusy ? 'warning' : isNonWorking ? 'default' : 'success';
                const chipLabel = isBusy
                  ? t('agenda.busy')
                  : holidayLabel
                    ? t('agenda.holiday')
                    : isNonWorking
                      ? t('agenda.non_working_day')
                      : t('agenda.free');
                return (
                  <Paper
                    key={dayKey}
                    variant="outlined"
                    sx={{
                      minHeight: 84,
                      p: 1,
                      opacity: publicAgendaView === 'month' && !isCurrentMonth ? 0.45 : 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {day.getDate()}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        color={chipColor}
                        label={chipLabel}
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </>
        )}
      </Paper>

      {/* Квалификации и опит */}
      <Paper elevation={3} sx={{ p: 3, my: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('profile.qualifications')}
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(editedData.qualifications || []).map((qual, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VerifiedUser color="primary" />
                    <Typography variant="body1" component="div" sx={{ flexGrow: 1 }}>
                      {qual}
                    </Typography>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveItem('qualification', index)}
                      aria-label={t('common.delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setDialogType('qualification');
                    setOpenDialog(true);
                  }}
                  sx={{ alignSelf: 'flex-start', mt: 1 }}
                  data-testid="profile-add-qualification-button"
                >
                  {t('dialog.add_qualification')}
                </Button>
              </Box>
            ) : (
              <List>
                {professional.qualifications.map((qual, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <VerifiedUser color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={qual} />
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(editedData.experience || []).map((exp, index) => (
                  <Box
                    key={index}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" component="div">
                        {t('profile.experience')} #{index + 1}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveExperienceItem(index)}
                        aria-label={t('common.delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <TextField
                      fullWidth
                      label={t('profile.position')}
                      value={exp?.title || ''}
                      onChange={(e) => handleExperienceChange(index, 'title', e.target.value)}
                      sx={{ mb: 1 }}
                      inputProps={{ 'data-testid': `profile-experience-title-input-${index}` }}
                    />
                    <TextField
                      fullWidth
                      label={t('profile.company')}
                      value={exp?.company || ''}
                      onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                      sx={{ mb: 1 }}
                      inputProps={{ 'data-testid': `profile-experience-company-input-${index}` }}
                    />
                    <TextField
                      fullWidth
                      label={t('profile.period')}
                      value={exp?.period || ''}
                      onChange={(e) => handleExperienceChange(index, 'period', e.target.value)}
                      inputProps={{ 'data-testid': `profile-experience-period-input-${index}` }}
                    />
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddExperienceItem}
                  sx={{ alignSelf: 'flex-start' }}
                  data-testid="profile-add-experience-button"
                >
                  {t('common.add')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Work color="primary" sx={{ mr: 1 }} />
                <Typography variant="body1" component="div">
                  {professional.experience.map((exp, index) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="subtitle1" component="div">
                        {exp.title} - {exp.company}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        {exp.period}
                      </Typography>
                    </Box>
                  ))}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Галерия с проекти */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {t('profile.portfolio')}
          </Typography>
          {isOwner && isEditing && (
            <Box>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={(e) => {
                  // Определяме типа на качването според data-атрибута
                  const uploadType = e.target.getAttribute('data-upload-type');
                  if (uploadType === 'avatar') {
                    handleAvatarFileUpload(e);
                  } else {
                    handleFileUpload(e);
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenFileDialog}
                disabled={uploadProgress !== null}
              >
                {uploadProgress !== null ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} variant="determinate" value={uploadProgress} />
                    {uploadProgress}%
                  </Box>
                ) : (
                  t('profile.add_portfolio')
                )}
              </Button>
            </Box>
          )}
        </Box>
        
        {professional.portfolio.length > 0 ? (
          <Grid container spacing={2}>
            {professional.portfolio.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box 
                    sx={{ 
                      position: 'relative',
                      paddingTop: '75%', // 4:3 Aspect Ratio
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleOpenImageModal(item)}
                  >
                    <Box
                      component="img"
                      src={item.img}
                      alt={item.description}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)'
                        }
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        p: 1,
                        display: 'flex',
                        gap: 1
                      }}
                    >
                      <Tooltip title={t('common.delete')} arrow>
                        <IconButton
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.7)' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveItem('portfolio', index);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.view')} arrow>
                        <IconButton
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                          }}
                        >
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" component="div" noWrap>
                      {item.title || item.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {isOwner && isEditing 
                ? t('profile.no_portfolio_add') 
                : t('profile.no_portfolio')}
            </Typography>
            {isOwner && isEditing && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenFileDialog}
                sx={{ mt: 2 }}
              >
                {t('profile.add_portfolio')}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Отзиви */}
      {professional?.reviews && professional.reviews.length > 0 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('profile.reviews')}
          </Typography>
          <Grid container spacing={2}>
            {professional.reviews.map((review, index) => (
              <Grid item xs={12} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">{review.author}</Typography>
                        {review.verified && (
                          <Chip
                            size="small"
                            color="success"
                            label={t('profile.verified_review')}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {review.date}
                      </Typography>
                    </Box>
                    <Rating value={review.rating} size="small" readOnly />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {review.comment}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Диалог за добавяне на нов елемент */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>
          {dialogType === 'service' && t('dialog.add_service')}
          {dialogType === 'qualification' && t('dialog.add_qualification')}
          {dialogType === 'portfolio' && t('dialog.add_portfolio')}
          {dialogType === 'avatar' && t('dialog.change_avatar')}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'avatar' ? (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label={t('profile.avatar_url')}
                value={editedData.avatar_url || ''}
                onChange={(e) => handleChange('avatar_url', e.target.value)}
                helperText={t('dialog.paste_image_url')}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('dialog.or_upload_image')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleOpenFileDialog}
                >
                  {t('common.upload')}
                </Button>
              </Box>
            </Box>
          ) : (
            <TextField
              autoFocus
              margin="dense"
              label={t('common.name')}
              fullWidth
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              inputProps={{ 'data-testid': 'profile-dialog-input' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          {dialogType === 'avatar' ? (
            <Button 
              onClick={handleAddItem}
              variant="contained"
            >
              {t('common.save')}
            </Button>
          ) : (
            <Button 
              onClick={handleAddItem}
              disabled={!newItem}
              variant="contained"
              data-testid="profile-dialog-confirm-button"
            >
              {t('common.add')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Модален прозорец за преглед на снимка */}
      <Modal
        open={openImageModal}
        onClose={handleCloseImageModal}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Fade in={openImageModal}>
          <Box
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: 24,
              p: 1,
              outline: 'none',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={selectedImage?.img}
                alt={selectedImage?.description}
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: 'calc(90vh - 100px)',
                  margin: '0 auto',
                  objectFit: 'contain'
                }}
              />
              <IconButton
                onClick={handleCloseImageModal}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" component="div">
                {selectedImage?.title || selectedImage?.description}
              </Typography>
              <Typography variant="body2" component="div" color="text.secondary">
                {selectedImage?.description}
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {!isOwner && user && professional && (
        <ReportDialog
          open={isReportDialogOpen}
          onClose={() => setIsReportDialogOpen(false)}
          targetType="profile"
          targetId={Number(professional.id)}
          targetLabel={t('admin.reports.target_label_profile', { id: professional.id })}
        />
      )}
    </Container>
  );
};

export default ProfessionalProfile; 